import { generateShortNameAndBrand } from 'actions'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import CheckAllIcon from 'components/ReusableComponents/Icons/CheckAll'
import Trash from 'components/ReusableComponents/Icons/Trash'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import { userRoleLevels, userRoles } from 'features/UserPermissions'
import Column from 'features/UserPermissions/UserPermissionColumn'
import SubColumn from 'features/UserPermissions/UserPermissionSubColumn'
import { useEffect, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import rolesApi from 'services/api/roles'

import styles from './GroupLevelControls.module.scss'

function sortByStateCity(a, b) {
  if (a.state === b.state) {
    return a.city.localeCompare(b.city)
  }

  return a.state.localeCompare(b.state)
}

const HotelNameCell = ({ hotelName, displayId }) => {
  const { brand, shortName } = generateShortNameAndBrand(hotelName)

  return (
    <div className={styles.hotelCell}>
      <p className={styles.hotelBrand}>{brand}</p>
      <p className={styles.shortHotelName}>
        <span className={styles.hotelDisplayId}>#{displayId}</span>
        {shortName}
      </p>
    </div>
  )
}

const GroupLevelControls = ({ userId, onClose }) => {
  const [userName, setUserName] = useState('')
  const [groupName, setGroupName] = useState('')

  const [isLoading, setIsLoading] = useState(false)
  const [isErred, setIsErred] = useState(false)
  const [errorDetail, setErrorDetail] = useState('')

  const { addToast } = useToasts()

  const [permissions, setPermissions] = useState()

  useEffect(() => {
    setIsLoading(true)
    rolesApi
      .getUserRolesForAllHotels(userId)
      .then(res => {
        setGroupName(res.groupName)
        setUserName(res.username)
        setPermissions(res.subsidiaries)
      })
      .catch(err => {
        setIsErred(true)
        setErrorDetail(err.toString())
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [userId])

  const [permissionsGroupedByCities, setPermissionsGroupedByCities] = useState()

  useEffect(() => {
    if (permissions) {
      const groupedByCities = permissions.reduce((prev, curr) => {
        const { city, state } = curr
        const key = `${city}, ${state}`

        if (!prev[key]) {
          prev[key] = []
        }

        prev[key].push(curr)

        return prev
      }, {})

      setPermissionsGroupedByCities(groupedByCities)
    }
  }, [permissions])

  function getSingleRole(roleName, data) {
    return data.map(u => ({
      ...u.roles.find(r => r.role === roleName),
      userId: u.id,
      name: u.name,
      hasCampaignsManageAccess: [
        userRoleLevels.HasGroupLevelPermission,
        userRoleLevels.HasPermission
      ].includes(u.roles.find(r => r.role === 'CampaignManage').accessLevel)
    }))
  }

  function updateRole(hotelId, role, accessLevel) {
    const updatedPermissions = permissions.map(hotel => {
      if (hotel.id === hotelId) {
        return {
          ...hotel,
          roles: hotel.roles.map(r => {
            if (r.role === role) {
              return {
                ...r,
                accessLevel
              }
            }

            return r
          })
        }
      }

      return hotel
    })

    setPermissions(updatedPermissions)
  }

  function handleChangeGroupLevelPermission(role, accessLevel) {
    const updatedPermissions = permissions.map(hotel => {
      return {
        ...hotel,
        roles: hotel.roles.map(r => {
          if (r.role === role) {
            return {
              ...r,
              accessLevel
            }
          }

          return r
        })
      }
    })

    setTimeout(() => {
      setPermissions(updatedPermissions)
    }, 10)
  }

  function checkAllPermissions(hotelId) {
    const updatedPermissions = permissions.map(hotel => {
      if (hotel.id === hotelId) {
        return {
          ...hotel,
          roles: hotel.roles.map(r => {
            if (r.accessLevel === userRoleLevels.HasGroupLevelPermission) {
              return {
                ...r
              }
            }

            if (r.role === 'ChatSalesBefore') {
              return { ...r }
            }

            return {
              ...r,
              accessLevel: userRoleLevels.HasPermission
            }
          })
        }
      }

      return hotel
    })

    setPermissions(updatedPermissions)
  }

  function uncheckAllPermissions(hotelId) {
    const updatedPermissions = permissions.map(hotel => {
      if (hotel.id === hotelId) {
        return {
          ...hotel,
          roles: hotel.roles.map(r => {
            return {
              ...r,
              accessLevel: userRoleLevels.NoPermission
            }
          })
        }
      }

      return hotel
    })

    setPermissions(updatedPermissions)
  }

  function handleConfirm() {
    setIsLoading(true)

    rolesApi
      .updateUserRolesForAllHotels({ userId, subsidiaries: permissions })
      .then(() => {
        addToast('User permissions updated', {
          appearance: 'success',
          autoDismiss: true
        })
      })
      .catch(() => {
        addToast('Error updating user permissions', {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        setIsLoading(false)
        onClose()
      })
  }

  if (!permissionsGroupedByCities) {
    return null
  }

  if (isLoading) {
    return <LoadingIndicator white={true} />
  }

  if (isErred) {
    return (
      <ConfirmationDialog
        title="Something went wrong while getting permissions"
        onClose={onClose}
      >
        <p>{errorDetail}</p>
      </ConfirmationDialog>
    )
  }

  return (
    <ConfirmationDialog
      maxWidth={1200}
      theme="blacklist"
      title={`${userName} in ${groupName}`}
      positiveMessage="Save changes"
      negativeMessage="Cancel"
      onClose={onClose}
      onConfirm={handleConfirm}
    >
      <div style={{ marginTop: -20 }}>
        {Object.keys(permissionsGroupedByCities)
          .sort((a, b) =>
            sortByStateCity(
              permissionsGroupedByCities[a][0],
              permissionsGroupedByCities[b][0]
            )
          )
          .map((city, i) => (
            <div key={city}>
              <div className={styles.city} style={{ top: i === 0 ? 74 : 0 }}>
                {city}
              </div>
              <div style={{ display: 'flex' }}>
                <div>
                  {permissionsGroupedByCities[city].map((hotel, y) => (
                    <div
                      className={styles.hotel}
                      style={{ marginTop: i === 0 && y === 0 ? 78 : 0 }}
                    >
                      <div className={styles.hotelName}>
                        <HotelNameCell
                          hotelName={hotel.name}
                          displayId={hotel.displayId}
                        />
                      </div>
                      <div className={styles.controls}>
                        <button onClick={() => checkAllPermissions(hotel.id)}>
                          <CheckAllIcon />
                        </button>
                        <button onClick={() => uncheckAllPermissions(hotel.id)}>
                          <Trash />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <Column showHeader={i === 0} category="Guest messaging">
                  <SubColumn
                    showHeader={i === 0}
                    title="During stay"
                    userPermissions={getSingleRole(
                      userRoles.ChatInProperty,
                      permissionsGroupedByCities[city]
                    )}
                    updateRole={updateRole}
                    checkboxHeight={76}
                  />
                  <SubColumn
                    showHeader={i === 0}
                    title="Before stay"
                    userPermissions={getSingleRole(
                      userRoles.ChatSalesBefore,
                      permissionsGroupedByCities[city]
                    )}
                    updateRole={updateRole}
                    checkboxHeight={76}
                  />
                  <SubColumn
                    showHeader={i === 0}
                    title="Reviews"
                    userPermissions={getSingleRole(
                      userRoles.ChatSalesAfter,
                      permissionsGroupedByCities[city]
                    )}
                    updateRole={updateRole}
                    checkboxHeight={76}
                  />
                  <SubColumn
                    showHeader={i === 0}
                    title="Direct"
                    userPermissions={getSingleRole(
                      userRoles.ChatDirect,
                      permissionsGroupedByCities[city]
                    )}
                    updateRole={updateRole}
                    checkboxHeight={76}
                  />
                </Column>
                <Column showHeader={i === 0} category="Reports">
                  <SubColumn
                    showHeader={i === 0}
                    title="View"
                    userPermissions={getSingleRole(
                      userRoles.ReportsView,
                      permissionsGroupedByCities[city]
                    )}
                    updateRole={updateRole}
                    checkboxHeight={76}
                  />
                </Column>
                <Column showHeader={i === 0} category="Automated messages">
                  <SubColumn
                    showHeader={i === 0}
                    title="View"
                    userPermissions={getSingleRole(
                      userRoles.CampaignView,
                      permissionsGroupedByCities[city]
                    )}
                    updateRole={updateRole}
                    checkboxHeight={76}
                  />
                  <SubColumn
                    showHeader={i === 0}
                    title="Manage"
                    userPermissions={getSingleRole(
                      userRoles.CampaignManage,
                      permissionsGroupedByCities[city]
                    )}
                    updateRole={updateRole}
                    checkboxHeight={76}
                    handleUpgradeClick={(_hotelId, _name, role) =>
                      handleChangeGroupLevelPermission(
                        role,
                        userRoleLevels.HasGroupLevelPermission
                      )
                    }
                    handleDowngradeClick={(_hotelId, _name, role) =>
                      handleChangeGroupLevelPermission(role, userRoleLevels.HasPermission)
                    }
                  />
                </Column>
                <Column showHeader={i === 0} category="Billing">
                  <SubColumn
                    showHeader={i === 0}
                    title="Manage"
                    userPermissions={getSingleRole(
                      userRoles.Billing,
                      permissionsGroupedByCities[city]
                    )}
                    updateRole={updateRole}
                    checkboxHeight={76}
                    handleUpgradeClick={(_hotelId, _name, role) =>
                      handleChangeGroupLevelPermission(
                        role,
                        userRoleLevels.HasGroupLevelPermission
                      )
                    }
                    handleDowngradeClick={(_hotelId, _name, role) =>
                      handleChangeGroupLevelPermission(role, userRoleLevels.HasPermission)
                    }
                  />
                </Column>
                <Column showHeader={i === 0} category="Users">
                  <SubColumn
                    showHeader={i === 0}
                    title="Manage"
                    userPermissions={getSingleRole(
                      userRoles.UserManage,
                      permissionsGroupedByCities[city]
                    )}
                    updateRole={updateRole}
                    checkboxHeight={76}
                    handleUpgradeClick={(_hotelId, _name, role) =>
                      handleChangeGroupLevelPermission(
                        role,
                        userRoleLevels.HasGroupLevelPermission
                      )
                    }
                    handleDowngradeClick={(_hotelId, _name, role) =>
                      handleChangeGroupLevelPermission(role, userRoleLevels.HasPermission)
                    }
                  />
                </Column>
              </div>
            </div>
          ))}
      </div>
    </ConfirmationDialog>
  )
}

export default GroupLevelControls
